import { Injectable } from '@angular/core';
import { fromJS, List, Map } from 'immutable';
import { isEmpty, truncate } from 'lodash';
import { Appointment } from '../appointment-data/models/appointment.model';
import { Client } from '../client-data/client.model';
import {
  DepositTypesEnum,
  DocumentReferenceTypesEnum,
  DOCUMENTS_CREATE_ROUTE,
  DOCUMENTS_PREVIEW_ROUTE,
  DOCUMENTS_SEND_ROUTE,
  MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
} from '../document-data/document-data.data';
import { Document } from '../document-data/document.model';
import { Billing } from '../document-data/sub-models/billing.model';
import { DocumentItem } from '../document-data/sub-models/document-item.model';
import { Reference } from '../document-data/sub-models/reference.model';
import { Shipping } from '../document-data/sub-models/shipping.model';
import { ItemUnitTypesEnum } from '../item-data/item-data.data';
import { DocumentPresetSettings } from '../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IRouter } from '../shared/interfaces/router.interface';
import { AmPmTimePipe } from '../shared/pipes/am-pm-time/am-pm-time.pipe';
import {
  DateTimeService,
  MOMENT_LONG_WEEKDAY_DATE_TIME_FORMAT,
} from 'i2g-ng-utils';
import { TrackedTime } from '../tracked-time-data/models/tracked-time.model';
import { DepositPayment } from '../document-data/sub-models/deposit-payment.model';
import { CalculationResult } from '../document-data/sub-models/calculation-result.model';
import { CalculatorService } from '../shared/services/calculator/calculator.service';
import { Deposit } from '../document-data/sub-models/deposit.model';

@Injectable()
export class DocumentDetailBuilder {
  constructor(
    private _dateTimeService: DateTimeService,
    private _calculatorService: CalculatorService,
    private _amPmTimePipe: AmPmTimePipe
  ) {}

  buildTabs(isNew: boolean): List<IRouter> {
    return List([
      {
        label: isNew ? 'ACTION.CREATE' : 'ACTION.EDIT',
        route: [DOCUMENTS_CREATE_ROUTE],
        // use queryParamsHandling: preserve to keep the query params such as client_id and job_id between tabs
        navigationExtras: { replaceUrl: true, queryParamsHandling: 'preserve' },
        e2eId: 'document-detail-create',
      },
      {
        label: 'ACTION.PREVIEW',
        route: [DOCUMENTS_PREVIEW_ROUTE],
        navigationExtras: { replaceUrl: true, queryParamsHandling: 'preserve' },
        e2eId: 'document-detail-preview',
      },
      {
        label: 'ACTION.SEND',
        route: [DOCUMENTS_SEND_ROUTE],
        navigationExtras: { replaceUrl: true, queryParamsHandling: 'preserve' },
        e2eId: 'document-detail-send',
      },
    ]);
  }

  buildDocumentWithClient(document: Document, client: Client): Document {
    const referenceProps = {
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.client,
      [Reference.KEYS.ID]: client.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: client.localId,
      [Reference.KEYS.REVISION_ID]: client.revisionId,
    };
    const billingProps = {
      [Billing.KEYS.NAME]: client.billingName,
      [Billing.KEYS.EMAIL]: client.email,
      [Billing.KEYS.ADDRESS]: client.billingAddress,
      [Billing.KEYS.PHONE]: client.phone,
      [Billing.KEYS.MOBILE]: client.mobile,
      [Billing.KEYS.REFERENCE]: referenceProps,
    };
    const shippingProps = {
      [Shipping.KEYS.ADDRESS]: client.shippingAddress,
    };
    const documentContentProps: any = {
      [Document.CONTENT_KEYS.BILLING]: billingProps,
      [Document.CONTENT_KEYS.SHIPPING]: shippingProps,
    };

    if (client.terms !== null) {
      documentContentProps[Document.CONTENT_KEYS.SETTINGS] = {
        [DocumentPresetSettings.KEYS.TERMS]: client.terms,
      };
    }

    return document.mergeContent(fromJS(documentContentProps));
  }

  buildDocumentItemPropsFromTrackedTime(
    trackedTime: TrackedTime,
    settings: DocumentPresetSettings
  ): Map<string, any> {
    const startedDate = this._dateTimeService.formatDate(
      trackedTime.startDate,
      MOMENT_LONG_WEEKDAY_DATE_TIME_FORMAT
    );
    const description = `${startedDate}${
      !isEmpty(trackedTime.notes) ? '\n\n' + trackedTime.notes : ''
    }`;
    const isQtyAndRateOn = settings.showQuantityAndRate;
    const qty = isQtyAndRateOn ? trackedTime.duration / 3600 : 1;
    const unitType = isQtyAndRateOn
      ? ItemUnitTypesEnum.labor_hours
      : ItemUnitTypesEnum.labor;
    const referenceProps = {
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.tracked_time,
      [Reference.KEYS.ID]: trackedTime.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: trackedTime.localId,
      [Reference.KEYS.REVISION_ID]: trackedTime.revisionId,
    };

    return fromJS({
      [DocumentItem.KEYS.DESCRIPTION]: truncate(description, {
        length: MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
      }),
      [DocumentItem.KEYS.UNIT_PRICE]: 0,
      [DocumentItem.KEYS.QTY]: +qty.toFixed(4),
      [DocumentItem.KEYS.UNIT_TYPE]: unitType,
      [DocumentItem.KEYS.REFERENCE]: referenceProps,
    });
  }

  buildDocumentItemPropsFromAppointment(
    appointment: Appointment,
    settings: DocumentPresetSettings
  ): Map<string, any> {
    const startDate = appointment.startDate;
    const duration = appointment.duration;
    const notes = appointment.notes;
    const isQtyAndRateOn = settings.showQuantityAndRate;
    const startDateAndTime = this._dateTimeService.formatDate(
      startDate,
      MOMENT_LONG_WEEKDAY_DATE_TIME_FORMAT
    );
    const endTime = this._amPmTimePipe.transform(
      this._dateTimeService.addTime(
        startDate,
        duration,
        'seconds',
        null,
        false,
        false
      )
    );
    const dateAndTimeDescription = startDateAndTime + ' - ' + endTime;
    const notesDescription = !isEmpty(notes) ? '\n\n' + notes : '';
    const description = `${dateAndTimeDescription}${notesDescription}`;
    const qty = isQtyAndRateOn ? duration / 3600 : 1;
    const unitType = isQtyAndRateOn
      ? ItemUnitTypesEnum.labor_hours
      : ItemUnitTypesEnum.labor;
    const referenceProps = {
      [Reference.KEYS.TYPE]: DocumentReferenceTypesEnum.appointment,
      [Reference.KEYS.ID]: appointment.id,
      [Reference.KEYS.REFERENCE_LOCAL_ID]: appointment.localId,
      [Reference.KEYS.REVISION_ID]: appointment.revisionId,
    };

    return fromJS({
      [DocumentItem.KEYS.DESCRIPTION]: truncate(description, {
        length: MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
      }),
      [DocumentItem.KEYS.UNIT_PRICE]: 0,
      [DocumentItem.KEYS.QTY]: +qty.toFixed(4),
      [DocumentItem.KEYS.UNIT_TYPE]: unitType,
      [DocumentItem.KEYS.REFERENCE]: referenceProps,
    });
  }

  buildDocumentWithDocumentItem(
    document: Document,
    documentItem: DocumentItem
  ): Document {
    const documentContentProps = {
      [Document.CONTENT_KEYS.ITEMS]: [documentItem],
    };

    return document.mergeContent(fromJS(documentContentProps));
  }

  buildDocumentWithDeposit(document: Document, deposit: Deposit) {
    const documentContentProps = {
      [Document.CONTENT_KEYS.DEPOSIT]: deposit,
    };

    return document.mergeContent(fromJS(documentContentProps));
  }

  buildDueDate(docDate: string, daysAmount: number) {
    return this._dateTimeService.addTime(docDate, daysAmount, 'days');
  }

  buildDefaultDeposit(
    defaultPercentage: number,
    calculationResult: CalculationResult,
    documentDueDate: string
  ): Deposit {
    const depositProps = {
      [Deposit.KEYS.PERCENTAGE]: defaultPercentage,
      [Deposit.KEYS.TYPE]: DepositTypesEnum.percentage,
    };
    const deposit: Deposit = new Deposit(depositProps);
    const depositPayment: DepositPayment = this._calculatorService.calculateDepositAmountAndPercentage(
      deposit,
      calculationResult
    );
    const today = this._dateTimeService.formatDate();
    const docDueDateNumOfDaysFromToday = this._dateTimeService.calculateNumberOfUnitsFromDate(
      documentDueDate,
      today
    );

    return new Deposit({
      ...depositProps,
      [Deposit.KEYS.FIXED_AMOUNT]: depositPayment.totalAmount,
      [Deposit.KEYS.DUE_DATE]:
        docDueDateNumOfDaysFromToday > 0 ? today : documentDueDate,
    });
  }

  buildDocumentContentWithoutSignatures(): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.CLIENT_SIGNATURE]: null,
      [Document.CONTENT_KEYS.COMPANY_SIGNATURE]: null,
    });
  }

  buildDocumentSettingsPropsForBankTransfer(
    bankTransferEnabled: boolean
  ): Map<string, any> {
    return fromJS({
      [Document.CONTENT_KEYS.SETTINGS]: {
        [DocumentPresetSettings.KEYS
          .BANK_TRANSFER_DISABLED]: !bankTransferEnabled,
      },
    });
  }
}
